/**
 * Collapsible - A molecule component for expandable/collapsible sections.
 *
 * Provides smooth expand/collapse animation with customizable header and summary.
 * Useful for reducing visual density in complex forms.
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';

export interface CollapsibleProps {
  /**
   * The header content (always visible).
   */
  header: ReactNode;
  /**
   * Optional summary shown next to the header when collapsed.
   */
  summary?: ReactNode;
  /**
   * The collapsible content.
   */
  children: ReactNode;
  /**
   * Whether the section starts expanded.
   */
  defaultExpanded?: boolean;
  /**
   * Controlled expanded state.
   */
  isExpanded?: boolean;
  /**
   * Callback when expanded state changes.
   */
  onExpandedChange?: (isExpanded: boolean) => void;
  /**
   * Additional CSS classes for the container.
   */
  className?: string;
  /**
   * Additional CSS classes for the content wrapper.
   */
  contentClassName?: string;
  /**
   * Test ID for E2E testing. Applied to the header button.
   */
  'data-testid'?: string;
}

export function Collapsible({
  header,
  summary,
  children,
  defaultExpanded = true,
  isExpanded: controlledIsExpanded,
  onExpandedChange,
  className,
  contentClassName,
  'data-testid': testId,
}: CollapsibleProps) {
  const [internalIsExpanded, setInternalIsExpanded] = useState(defaultExpanded);
  const innerContentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);

  const isControlled = controlledIsExpanded !== undefined;
  const isExpanded = isControlled ? controlledIsExpanded : internalIsExpanded;

  // Track when expand animation completes so we can remove overflow-hidden.
  // This prevents clipping of borders, shadows, and rounded corners on content.
  // Seeded from the ACTUAL initial expanded state — not defaultExpanded, which
  // is meaningless for a controlled instance: a controlled Collapsible mounting
  // collapsed would otherwise skip overflow-hidden, leaving its zero-height
  // content un-clipped — an invisible (opacity-0, still hit-testable) panel
  // swallowing clicks on whatever renders below it.
  const [expandComplete, setExpandComplete] = useState(isExpanded);

  // Re-apply overflow-hidden when collapse starts. Adjusting this state during
  // render (rather than in a layout effect) keeps the reset synchronous and
  // before paint, while remaining pure (only runs on the transition to collapsed).
  // See https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevIsExpanded, setPrevIsExpanded] = useState(isExpanded);
  if (isExpanded !== prevIsExpanded) {
    setPrevIsExpanded(isExpanded);
    if (!isExpanded) {
      setExpandComplete(false);
    }
  }

  // Measure content height for smooth animation using ResizeObserver
  // We observe the INNER div (that grows with content), not the outer overflow-hidden container
  useEffect(() => {
    const element = innerContentRef.current;
    if (!element) return;

    const updateHeight = () => {
      // Use offsetHeight to get the actual rendered height of the inner content
      setContentHeight(element.offsetHeight);
    };

    // Initial measurement
    updateHeight();

    // Watch for size changes (items added/removed, content changes)
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleToggle = () => {
    const newValue = !isExpanded;
    if (isControlled && onExpandedChange) {
      onExpandedChange(newValue);
    } else {
      setInternalIsExpanded(newValue);
    }
  };

  return (
    <div className={cn('', className)}>
      {/* Header Row */}
      <button
        type="button"
        onClick={handleToggle}
        className="
          -mx-2 flex w-full items-center justify-between gap-3 rounded-lg p-2 text-left transition-colors
          hover:bg-bg-muted/50
          dark:hover:bg-bg-elevated/50
        "
        aria-expanded={isExpanded}
        data-testid={testId}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {header}
          {/* Summary - only show when collapsed */}
          {!isExpanded && summary && (
            <span
              className="
                truncate text-sm text-text-muted
                dark:text-text-muted
              "
            >
              {summary}
            </span>
          )}
        </div>
        <ChevronDown
          className={cn(
            `
              size-5 shrink-0 text-text-muted transition-transform duration-200
              dark:text-text-muted
            `,
            isExpanded && 'rotate-180',
          )}
        />
      </button>

      {/* Content with height animation */}
      <div
        data-testid="collapsible-content"
        className={cn(
          'transition-all duration-200 ease-in-out',
          !expandComplete && `overflow-hidden`,
          // Collapsed content is invisible but opacity-0 keeps it hit-testable;
          // make it inert so it can never intercept clicks meant for content below.
          !isExpanded && 'pointer-events-none',
        )}
        style={{
          maxHeight: isExpanded ? (contentHeight ?? 'none') : 0,
          opacity: isExpanded ? 1 : 0,
        }}
        onTransitionEnd={(e) => {
          if (e.propertyName === 'max-height' && isExpanded) {
            setExpandComplete(true);
          }
        }}
      >
        <div ref={innerContentRef} className={cn('pt-3', contentClassName)}>
          {children}
        </div>
      </div>
    </div>
  );
}
